import { load } from '@progress/kendo-react-intl'
import currencyData from 'cldr-core/supplemental/currencyData.json'
import likelySubtags from 'cldr-core/supplemental/likelySubtags.json'

import esCurrencies from 'cldr-numbers-full/main/es/currencies.json'
import esNumbers from 'cldr-numbers-full/main/es/numbers.json'
import esCOCurrencies from 'cldr-numbers-full/main/es-CO/currencies.json'
import esCONumbers from 'cldr-numbers-full/main/es-CO/numbers.json'
import esECCurrencies from 'cldr-numbers-full/main/es-EC/currencies.json'
import esECNumbers from 'cldr-numbers-full/main/es-EC/numbers.json'
import esPECurrencies from 'cldr-numbers-full/main/es-PE/currencies.json'
import esPENumbers from 'cldr-numbers-full/main/es-PE/numbers.json'

import { getRuntimeConfig } from './runtimeConfig'

load(
  likelySubtags,
  currencyData,
  esNumbers,
  esCurrencies,
  esPENumbers,
  esPECurrencies,
  esCONumbers,
  esCOCurrencies,
  esECNumbers,
  esECCurrencies,
)

interface CurrencyConfig {
  currency: string
  locale: string
  currencyDisplay: 'symbol'
  minimumFractionDigits: number
  maximumFractionDigits: number
}

type RegionConfig = {
  [key: string]: { locale: string; currency: string }
}

export const regionConfig: RegionConfig = {
  Peru: { locale: 'es-PE', currency: 'PEN' },
  Colombia: { locale: 'es-CO', currency: 'COP' },
  Ecuador: { locale: 'es-EC', currency: 'USD' },
}

const currencyConfig = Object.entries(regionConfig).reduce<Record<string, CurrencyConfig>>(
  (acc, [_, { locale, currency }]) => ({
    ...acc,
    [locale]: {
      currency,
      locale,
      currencyDisplay: 'symbol',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
  }),
  {},
)

/**
 * Locale que corresponde a la región configurada en runtime. Es la única fuente de verdad:
 * la capa de localización de Kendo (ConfigLanguageContainer) también la usa, para que los
 * importes formateados y los que se teclean en los NumericTextBox coincidan.
 */
export const getLocaleDeRegion = (region?: string): string =>
  (regionConfig[region ?? getRuntimeConfig().REGION] ?? regionConfig.Ecuador).locale

export const getCurrencyConfig = (locale?: string): CurrencyConfig => {
  const currentRegion = getRuntimeConfig().REGION
  const defaultRegionConfig = regionConfig[currentRegion as keyof RegionConfig] ?? regionConfig.Ecuador
  const defaultLocale = defaultRegionConfig.locale

  return currencyConfig[locale ?? defaultLocale] || currencyConfig[defaultLocale]
}

export const formatCurrency = (value: number, locale?: string): string => {
  const config = getCurrencyConfig(locale)

  // Se delega en Intl con el locale de la región. Antes se formateaba con 'en-US' y después
  // se intercambiaban los separadores a mano, lo que producía siempre el estilo ecuatoriano
  // (1.234,56): correcto para es-EC, invertido para es-PE, que usa punto decimal y coma de
  // miles. Verificado que para es-EC/USD la salida es idéntica a la anterior.
  return new Intl.NumberFormat(config.locale, {
    style: 'currency',
    currency: config.currency,
    currencyDisplay: config.currencyDisplay,
    minimumFractionDigits: config.minimumFractionDigits,
    maximumFractionDigits: config.maximumFractionDigits,
  }).format(value)
}

export const parseCurrency = (value: string, locale?: string): number => {
  const config = getCurrencyConfig(locale)

  // Los separadores dependen del locale: es-EC usa '.' de miles y ',' decimal, y es-PE al revés.
  // Se consultan a Intl en vez de asumirlos, que es lo que hacía la versión anterior.
  const partes = new Intl.NumberFormat(config.locale).formatToParts(11111.1)
  const separadorMiles = partes.find((p) => p.type === 'group')?.value ?? '.'
  const separadorDecimal = partes.find((p) => p.type === 'decimal')?.value ?? ','

  const sinMiles = value.split(separadorMiles).join('')
  const normalizado = sinMiles.split(separadorDecimal).join('.').replace(/[^0-9.-]/g, '')

  return Number(normalizado)
}

export const defaultConfig = {
  get locale() { return (regionConfig[getRuntimeConfig().REGION] ?? regionConfig.Ecuador).locale },
  get currency() { return (regionConfig[getRuntimeConfig().REGION] ?? regionConfig.Ecuador).currency },
}

interface ICurrencyConfigHook {
  formatCurrency: (value: number) => string
  parseCurrency: (value: string) => number
  currency: string
  locale: string
}

export const useCurrencyConfig = (locale?: string): ICurrencyConfigHook => {
  const config = getCurrencyConfig(locale)

  return {
    formatCurrency: (value: number) => formatCurrency(value, config.locale),
    parseCurrency: (value: string) => parseCurrency(value),
    currency: config.currency,
    locale: config.locale,
  }
}
